import { useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar } from "@/components/ui/avatar";
import { User, Building, Lock, Save, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import { UPDATE_PROFILE_MUTATION, USER_ACCOUNT_QUERY } from "@/Graphql/Queries";
import client from "@/Graphql/apolloClient";
import { useToast } from "@/hooks/use-toast";
import logger from '@/utils/logger';
const CHANGE_PASSWORD = gql`
  mutation changePassword($accessToken: String!, $newPassword: String!, $userId: String!) {
    changePassword(accessToken: $accessToken, newPassword: $newPassword, userId: $userId) {
      response {
        success
        message
      }
    }
  }
`;

// Form schemas
const personalInfoSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  username: z.string().min(3, "Username must be at least 3 characters"),
  phoneNumber: z.string().optional(),
  jobTitle: z.string().optional(),
});

const companyInfoSchema = z.object({
  companyName: z.string().min(1, "Company name is required"),
  companySize: z.string().optional(),
  industry: z.string().optional(),
  website: z.string().optional(),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: z
    .string()
    .refine((val) => val === "" || val.length >= 8, "Password must be at least 8 characters")
    .refine(
      (val) => val === "" || /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]+$/.test(val),
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    ),
  confirmPassword: z.string(),
}).refine((data) => {
  // Only validate password match if both fields have content
  if (data.newPassword === "" && data.confirmPassword === "") return true;
  if (data.newPassword === "" || data.confirmPassword === "") return true;
  return data.newPassword === data.confirmPassword;
}, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PersonalInfo = z.infer<typeof personalInfoSchema>;
type CompanyInfo = z.infer<typeof companyInfoSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

export default function Account() {
  const base_path = import.meta.env.VITE_APP_BASE_PATH || '';
  const { toast } = useToast();
  const userAccountData = localStorage.getItem('userAccountData');
  const parsedData = userAccountData ? JSON.parse(userAccountData) : [];

  const user = useMemo(() => parsedData[0] || {}, [userAccountData]);
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Personal Info Form
  const personalForm = useForm<PersonalInfo>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      username: user?.login || "",
      phoneNumber: user?.phone || "",
      jobTitle: user?.jobTitle || "N/A",
    },
  });

  // Company Info Form
  const companyForm = useForm<CompanyInfo>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      companyName: user?.userCompanyName || "",
      companySize: user?.companySize || "",
      industry: user?.industry || "N/A",
      website: "N/A",
      address: user?.userCompanyAddress || "",
      city: user?.userCity || "",
      state: user?.state || "N/A",
      postalCode: user?.postalCode || "N/A",
      country: parsedData[0]?.userCountry || "N/A",
    },
  });

  // Password Form with onBlur validation for better UX
  const passwordForm = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    mode: "onBlur", // Validate on blur instead of onChange for better UX
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  // Watch password fields to trigger revalidation
  const watchedNewPassword = passwordForm.watch("newPassword");
  const watchedConfirmPassword = passwordForm.watch("confirmPassword");

  // Trigger validation when either password field changes
  useEffect(() => {
    if (watchedNewPassword !== undefined || watchedConfirmPassword !== undefined) {
      passwordForm.trigger(["newPassword", "confirmPassword"]);
    }
  }, [watchedNewPassword, watchedConfirmPassword, passwordForm]);

  // GraphQL Mutations
  const [updateUser, { loading: updateUserLoading }] = useMutation(UPDATE_PROFILE_MUTATION);
  const [changePassword, { loading: changePasswordLoading }] = useMutation(CHANGE_PASSWORD);

  const token = localStorage.getItem('authToken');
  const userId = localStorage.getItem('userId');

  const onPersonalSubmit = async (data: PersonalInfo) => {
    if (updateUserLoading) return;

    const companyId = import.meta.env.VITE_APP_COMPANY_ID || user?.companyId;
    if (!companyId) {
      toast({
        title: "Error",
        description: "Company ID is required.",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await updateUser({
        variables: {
          input: {
            id: user?.id,
            accessToken: token,
            login: data.username,
            firstName: data.firstName,
            lastName: data.lastName,
            email: user?.email,
            activated: user?.activated || true,
            langKey: user?.langKey || 'en',
            accountType: user?.accountType || 'EXTERNAL',
            companyType: user?.companyType || 'MEDIA_BUYERS',
            platform: user?.platform || 'ECOMMERCE',
            companyId: companyId,
            admin: user?.admin || false,
            phone: data.phoneNumber,
            ecommerceDomainName: user?.ecommerceDomainName || "",
            userCompanyName: user?.userCompanyName || "",
            userCompanyId: user?.userCompanyId || "",
            userCompanyAddress: user?.userCompanyAddress || "",
            userCompanyPhoneNumber: user?.userCompanyPhoneNumber || "",
            rejectReason: "",
            jobTitle: data.jobTitle,
            userId: userId,
            userCountry: user?.country?.countryId || "",
            userCity: user?.userCountry || "",
            industry: user?.industry || "",
            country: {
              id: user?.country?.countryId || "",
              countryId: user?.country?.countryId || "",
              name: user?.country?.name || "",
              nameJa: user?.country?.name === "Malaysia" ? "\u30de\u30ec\u30fc\u30b7\u30a2" : user?.country?.name || "",
              latitude: user?.country?.name === "Malaysia" ? 3.766497 : -24.152823,
              longitude: user?.country?.name === "Malaysia" ? 102.253742 : 133.768253,
              zoom: 8,
              mediaOwnerTermsAndConditions: "null",
              buyerTermsAndConditions: "null",
              swLat: user?.country?.name === "Malaysia" ? 0.861 : -38.122505,
              swLng: user?.country?.name === "Malaysia" ? 99.644 : 113.641302,
              seLat: user?.country?.name === "Malaysia" ? 0.861 : -38.122505,
              seLng: user?.country?.name === "Malaysia" ? 119.267 : 154.114933,
              neLat: user?.country?.name === "Malaysia" ? 7.374 : 0,
              neLng: user?.country?.name === "Malaysia" ? 119.267 : 0,
              nwLat: user?.country?.name === "Malaysia" ? 7.374 : -11.055568,
              nwLng: user?.country?.name === "Malaysia" ? 99.644 : 113.641302,
              population: user?.country?.name === "Malaysia" ? 32657300 : 24669600,
              iso: user?.country?.name === "Malaysia" ? "MY" : "AU",
              postalformat: "99999",
              postalname: "Postal code",
              geopc: 1,
              active: true,
              dialingCode: user?.country?.name === "Malaysia" ? "+60" : "+61",
              timezone: "null",
              miDataSensorStatus: "null",
              tax: {
                label: user?.country?.name === "Malaysia" ? "SST" : "GST",
                percent: user?.country?.name === "Malaysia" ? 6.0 : 10,
              },
              region: "",
              weekends: "null",
              locale: "en",
              trCountryName: user?.country?.name || "",
              internetEmissionFactor: "null",
              carbonEmissionFactor: "null",
            },
          },
        },
      });

      if (response.data.updateUser && response.data.updateUser.message) {
        toast({
          title: "Success",
          description: response.data.updateUser.message || "Personal information updated successfully",
          variant: "default",
        });

        const { data: fetchedUserData } = await client.query({
          query: USER_ACCOUNT_QUERY,
          variables: { accessToken: token },
          fetchPolicy: 'network-only',
        });

        const updatedUser = fetchedUserData?.userAccount[0] || {};
        logger.log("Updated user data:", updatedUser);
          
        const parsedData = [updatedUser];
        localStorage.setItem('userAccountData', JSON.stringify(parsedData));
        localStorage.setItem('userName', `${updatedUser.firstName || data.firstName} ${updatedUser.lastName || data.lastName}`);

        personalForm.reset({
          firstName: updatedUser.firstName || data.firstName,
          lastName: updatedUser.lastName || data.lastName,
          username: updatedUser.login || data.username,
          phoneNumber: updatedUser.phone || data.phoneNumber,
          jobTitle: updatedUser.designation || data.jobTitle,
        });
      }
    } catch (err) {
      logger.error("Error updating user", err);
      toast({
        title: "Error",
        description: "Error updating profile",
        variant: "destructive",
      });
    }
  };

  const onPasswordSubmit = (data: PasswordForm) => {
    if (changePasswordLoading) return;

    changePassword({
      variables: {
        accessToken: token,
        newPassword: data.newPassword,
        userId: userId,
      },
    })
      .then(({ data: responseData }) => {
        logger.log("Password change response", responseData);

        if (responseData && responseData.changePassword.response.success) {
          toast({
            title: "Success",
            description: responseData.changePassword.response.message || "Password changed successfully",
            variant: "default",
          });
          passwordForm.reset();
        } else {
          toast({
            title: "Error",
            description: "Failed to update password",
            variant: "destructive",
          });
        }
      })
      .catch((err: any) => {
        logger.log("Error updating password", err);
        toast({
          title: "Error",
          description: "Error updating password",
          variant: "destructive",
        });
      });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <Link to={`${base_path}/my-campaigns`}>
            <Button
              variant="outline"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 bg-white/80 backdrop-blur-sm border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md transition-all duration-200"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to My Campaigns
            </Button>
          </Link>
        </div>

        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-600 to-emerald-200 rounded-full mb-6">
            <User className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-emerald-500 bg-clip-text text-transparent">
            Account Settings
          </h1>
          <p className="text-lg text-gray-600 mt-3 max-w-2xl mx-auto">
            Manage your personal information, company details, and security preferences
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="relative">
              <Avatar className="h-24 w-24 ring-4 ring-emerald-100 shadow-lg flex items-center justify-center text-4xl font-bold bg-gradient-to-r from-emerald-500 to-emerald-500 text-white">
                <span>{(user?.firstName?.[0] || "U") + (user?.lastName?.[0] || "")}</span>
              </Avatar>
            </div>
            <div className="text-center sm:text-left">
              <h2 className="text-2xl font-bold text-gray-900">{user?.firstName} {user?.lastName}</h2>
              <p className="text-emerald-600 font-medium">{user?.designation || "Member"}</p>
              <p className="text-gray-500">{user?.email}</p>
              <p className="text-sm text-gray-500">{user?.userCompanyName}</p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="personal" className="space-y-8">
          <TabsList className="grid w-full grid-cols-3 h-14 bg-white/50 backdrop-blur-sm rounded-xl p-1 shadow-lg border border-white/20">
            <TabsTrigger 
              value="personal" 
              className="rounded-lg font-medium data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-emerald-600"
            >
              <User className="h-4 w-4 mr-2" />
              Personal
            </TabsTrigger>
            <TabsTrigger 
              value="company" 
              className="rounded-lg font-medium data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-emerald-600"
            >
              <Building className="h-4 w-4 mr-2" />
              Company
            </TabsTrigger>
            <TabsTrigger 
              value="security" 
              className="rounded-lg font-medium data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:text-emerald-600"
            >
              <Lock className="h-4 w-4 mr-2" />
              Security
            </TabsTrigger>
          </TabsList>

          <TabsContent value="personal">
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-200 text-white">
                <CardTitle className="flex items-center text-xl">
                  <User className="h-6 w-6 mr-3 text-white" />
                  Personal Information
                </CardTitle>
                <p className="text-emerald-100 mt-2">Update your personal details and contact information</p>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={personalForm.handleSubmit(onPersonalSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">First Name</Label>
                      <Input
                        id="firstName"
                        {...personalForm.register("firstName")}
                        className={`h-12 rounded-xl border-2 transition-all duration-200 ${
                          personalForm.formState.errors.firstName
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-200 focus:border-green-500 focus:ring-green-500/20'
                        }`}
                      />
                      {personalForm.formState.errors.firstName && (
                        <p className="text-sm text-red-600">{personalForm.formState.errors.firstName.message}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">Last Name</Label>
                      <Input
                        id="lastName"
                        {...personalForm.register("lastName")}
                        className={`h-12 rounded-xl border-2 transition-all duration-200 ${
                          personalForm.formState.errors.lastName
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-200 focus:border-green-500 focus:ring-green-500/20'
                        }`}
                      />
                      {personalForm.formState.errors.lastName && (
                        <p className="text-sm text-red-600">{personalForm.formState.errors.lastName.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address</Label>
                    <div className="relative">
                      <Input
                        id="email"
                        type="email"
                        value={user?.email || ""}
                        disabled
                        className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000 pr-12"
                      />
                      <Lock className="absolute right-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    </div>
                    <p className="text-xs text-amber-600 flex items-center">
                      <Lock className="h-3 w-3 mr-1" />
                      Email cannot be changed for security reasons
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-sm font-medium text-gray-700">Username</Label>
                    <Input
                      id="username"
                      {...personalForm.register("username")}
                      disabled
                      className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phoneNumber" className="text-sm font-medium text-gray-700">Phone Number</Label>
                      <Input
                        id="phoneNumber"
                        {...personalForm.register("phoneNumber")}
                        placeholder="+1 (555) 123-4567"
                        className="h-12 rounded-xl border-2 border-gray-200 focus:border-green-500 focus:ring-green-500/20 transition-all duration-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jobTitle" className="text-sm font-medium text-gray-700">Job Title</Label>
                      <Input
                        id="jobTitle"
                        {...personalForm.register("jobTitle")}
                        disabled
                        placeholder="e.g. Marketing Manager"
                        className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000"
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      disabled={updateUserLoading}
                      className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      <Save className="h-5 w-5 mr-2" />
                      {updateUserLoading ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="company">
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-200 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Building className="h-6 w-6 mr-3 text-white" />
                  Company Information
                </CardTitle>
                <p className="text-emerald-100 mt-2">Manage your business details and company profile</p>
              </CardHeader>
              <CardContent className="p-8">
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="companyName" className="text-sm font-medium text-gray-700">Company Name</Label>
                      <Input
                        id="companyName"
                        {...companyForm.register("companyName")}
                        disabled
                        className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="industry" className="text-sm font-medium text-gray-700">Industry</Label>
                      <Input
                        id="industry"
                        {...companyForm.register("industry")}
                        disabled
                        placeholder="e.g. Technology"
                        className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website" className="text-sm font-medium text-gray-700">Website</Label>
                      <Input
                        id="website"
                        {...companyForm.register("website")}
                        disabled
                        placeholder="https://example.com"
                        className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">Address Information</h3>
                    <div className="space-y-2">
                      <Label htmlFor="address" className="text-sm font-medium text-gray-700">Street Address</Label>
                      <Input
                        id="address"
                        {...companyForm.register("address")}
                        disabled
                        placeholder="123 Business Street"
                        className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city" className="text-sm font-medium text-gray-700">City</Label>
                        <Input
                          id="city"
                          value={"N/A"}
                          disabled
                          className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state" className="text-sm font-medium text-gray-700">State/Province</Label>
                        <Input
                          id="city"
                          {...companyForm.register("city")}
                          disabled
                          className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-sm font-medium text-gray-700">Country</Label>
                      <Input
                        id="country"
                        {...companyForm.register("country")}
                        disabled
                        className="h-12 rounded-xl bg-gray-50 border-2 border-gray-200 text-gray-1000"
                      />
                    </div>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl rounded-2xl overflow-hidden">
              <CardHeader className="bg-gradient-to-r from-emerald-600 to-emerald-200 text-white">
                <CardTitle className="flex items-center text-xl">
                  <Lock className="h-6 w-6 mr-3 text-white" />
                  Security Settings
                </CardTitle>
                <p className="text-emerald-100 mt-2">Manage your account security and password settings</p>
              </CardHeader>
              <CardContent className="p-8">
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <Lock className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-amber-800">Password Security Tips</h4>
                      <ul className="text-sm text-amber-700 mt-1 space-y-1">
                        <li>• Use at least 8 characters with a mix of letters, numbers, and symbols</li>
                        <li>• Avoid using personal information or common words</li>
                        <li>• Consider using a password manager for stronger security</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <form onSubmit={passwordForm.handleSubmit(onPasswordSubmit)} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword" className="text-sm font-medium text-gray-700">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showCurrentPassword ? "text" : "password"}
                        {...passwordForm.register("currentPassword")}
                        className={`h-12 rounded-xl border-2 transition-all duration-200 pr-12 ${
                          passwordForm.formState.errors.currentPassword
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-200 focus:border-green-500 focus:ring-green-500/20'
                        }`}
                        placeholder="Enter your current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordForm.formState.errors.currentPassword && (
                      <p className="text-sm text-red-600">{passwordForm.formState.errors.currentPassword.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-sm font-medium text-gray-700">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        {...passwordForm.register("newPassword")}
                        className={`h-12 rounded-xl border-2 transition-all duration-200 pr-12 ${
                          passwordForm.formState.errors.newPassword
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-200 focus:border-green-500 focus:ring-green-500/20'
                        }`}
                        placeholder="Enter your new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordForm.formState.errors.newPassword && (
                      <p className="text-sm text-red-600">{passwordForm.formState.errors.newPassword.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        {...passwordForm.register("confirmPassword")}
                        className={`h-12 rounded-xl border-2 transition-all duration-200 pr-12 ${
                          passwordForm.formState.errors.confirmPassword
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : 'border-gray-200 focus:border-green-500 focus:ring-green-500/20'
                        }`}
                        placeholder="Confirm your new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordForm.formState.errors.confirmPassword && (
                      <p className="text-sm text-red-600">{passwordForm.formState.errors.confirmPassword.message}</p>
                    )}
                  </div>

                  <div className="pt-4">
                    <Button 
                      type="submit" 
                      disabled={changePasswordLoading}
                      className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-medium py-3 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                    >
                      <Lock className="h-5 w-5 mr-2" />
                      {changePasswordLoading ? "Updating..." : "Change Password"}
                    </Button>
                  </div>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="font-medium text-gray-800 mb-3">Account Security Status</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm text-gray-700">Email Verification</span>
                      <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">Verified ✓</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm text-gray-700">Two-Factor Authentication</span>
                      <span className="text-xs font-medium text-blue-700 bg-emerald-100 px-2 py-1 rounded-full">Coming Soon</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}